export const USER_TYPES = {
  DAM: "dam",
  JIOPOINT: "jiopoint",
  BOTH: "both",
};

export const USER_TYPE_FORM_OPTIONS = [
  { value: USER_TYPES.DAM, label: "DAM" },
  { value: USER_TYPES.JIOPOINT, label: "JioPoint" },
  { value: USER_TYPES.BOTH, label: "Both (DAM & JioPoint)" },
];

export const DEFAULT_HOME_ROUTE = "/dashboard-v2";
export const JIOPOINT_HOME_ROUTE = "/jiopoint/dashboard";
export const ACTIVE_USER_TYPE_STORAGE_KEY = "active_user_type";
export const SELECT_DASHBOARD_ROUTE = "/select-dashboard";
export const USER_TYPE_HEADER = "X-User-Type";

const VALID_TYPES = [USER_TYPES.DAM, USER_TYPES.JIOPOINT];

export function getUserTypes(user) {
  if (!user?.user_type) return [USER_TYPES.DAM];

  const raw = user.user_type;
  const list = Array.isArray(raw) ? raw : [raw];
  const filtered = list.filter((t) => VALID_TYPES.includes(t));

  return filtered.length > 0 ? filtered : [USER_TYPES.DAM];
}

export function hasUserType(user, type) {
  return getUserTypes(user).includes(type);
}

export function isDualOrgUser(user) {
  const types = getUserTypes(user);
  return types.includes(USER_TYPES.DAM) && types.includes(USER_TYPES.JIOPOINT);
}

export function isJioPointOnlyUser(user) {
  const types = getUserTypes(user);
  return types.length === 1 && types[0] === USER_TYPES.JIOPOINT;
}

export function isDamOnlyUser(user) {
  const types = getUserTypes(user);
  return types.length === 1 && types[0] === USER_TYPES.DAM;
}

export function resolveActiveUserType(user, storedActiveType) {
  const types = getUserTypes(user);

  if (storedActiveType && types.includes(storedActiveType)) {
    return storedActiveType;
  }

  if (types.length === 1) {
    return types[0];
  }

  return null;
}

export function normalizeUserType(user, storedActiveType) {
  return resolveActiveUserType(user, storedActiveType) ?? USER_TYPES.DAM;
}

export function isJioPointUser(user, storedActiveType) {
  return normalizeUserType(user, storedActiveType) === USER_TYPES.JIOPOINT;
}

export function getHomeRoute(userOrType) {
  const userType =
    typeof userOrType === "string"
      ? userOrType
      : normalizeUserType(userOrType);
  return userType === USER_TYPES.JIOPOINT
    ? JIOPOINT_HOME_ROUTE
    : DEFAULT_HOME_ROUTE;
}

export function formatUserTypeLabel(userType) {
  const types = getUserTypes({ user_type: userType });
  if (types.length > 1) return "Both";
  if (types[0] === USER_TYPES.JIOPOINT) return "JioPoint";
  return "DAM";
}

/** Map stored user_type to create/edit form select value. */
export function toUserTypeFormValue(userType) {
  const types = getUserTypes({ user_type: userType });
  if (types.length > 1) return USER_TYPES.BOTH;
  return types[0] ?? USER_TYPES.DAM;
}

/** Map form select value to user_type array for API payloads. */
export function fromUserTypeFormValue(formValue) {
  if (formValue === USER_TYPES.BOTH) {
    return [USER_TYPES.DAM, USER_TYPES.JIOPOINT];
  }
  return [formValue];
}

/** Options allowed when creating a user (non-superadmin must include active workspace). */
export function getUserTypeFormOptions(activeType, isSuperAdmin) {
  if (isSuperAdmin) return USER_TYPE_FORM_OPTIONS;
  if (activeType === USER_TYPES.JIOPOINT) {
    return USER_TYPE_FORM_OPTIONS.filter((o) => o.value !== USER_TYPES.DAM);
  }
  return USER_TYPE_FORM_OPTIONS.filter((o) => o.value !== USER_TYPES.JIOPOINT);
}

export function resolvePostLoginNavigation(user, storedActiveType) {
  const types = getUserTypes(user);

  if (types.length === 1) {
    const userType = types[0];
    return {
      kind: "navigate",
      userType,
      route: getHomeRoute(userType),
    };
  }

  if (isDualOrgUser(user)) {
    const active = resolveActiveUserType(user, storedActiveType);
    if (active) {
      return {
        kind: "navigate",
        userType: active,
        route: getHomeRoute(active),
      };
    }
    return {
      kind: "choose",
      route: SELECT_DASHBOARD_ROUTE,
    };
  }

  return {
    kind: "navigate",
    userType: USER_TYPES.DAM,
    route: DEFAULT_HOME_ROUTE,
  };
}
